import { useCallback, useMemo } from "react"

import { Flex } from "antd"
import dayjs from "dayjs"
import { useSelector } from "react-redux"

import { getCalculatedPeriod } from "../../../Utils/utils"
import { AlertsTypeToggle } from "../AlertsTypeToggle"
import CompareSelect from "../CompareSelect"
import DropdownDateBlock from "../DropdownDateBlock/DropdownDateBlock"
import { resolveCompare } from "./resolveCompare"

export const DashboardFiltersBlock = ({
  onlyPeriod,
  showCompare,
  filters,
  handleChangeFilter,
  disabled,
  placement
}) => {
  const isMobile = useSelector((state) => state.nav.isMobile)

  const handleChangeCustomDate = useCallback(
    (newFilters) => {
      // Apply without a complete range falls back to Last 4 weeks, with its preset id and its compare
      // re-checked like any other period change.
      const isReset = !newFilters?.period
      const nextFilters = isReset
        ? { period: "27 days", __preset_period: "last_4_weeks" }
        : newFilters
      const nextPeriod = nextFilters.period
      const wasAllTime = filters?.period === "all"
      const goingToAllTime = nextPeriod === "all"

      const buildCompareFields = () => {
        if (goingToAllTime) {
          return {
            lastCompare: filters?.compare || filters?.lastCompare || null,
            compare: null
          }
        }

        const sourceCompare = wasAllTime ? filters?.lastCompare : filters?.compare

        return {
          compare: resolveCompare(sourceCompare, nextPeriod),
          lastCompare: wasAllTime ? null : filters?.lastCompare
        }
      }

      const apply = () =>
        handleChangeFilter({
          ...nextFilters,
          ...(showCompare ? buildCompareFields() : {})
        })

      if (isReset) {
        setTimeout(apply, 100)
        return
      }

      apply()
    },
    [showCompare, filters?.period, filters?.compare, filters?.lastCompare, handleChangeFilter]
  )

  const compareCurrentValue = useMemo(() => {
    const calculated = getCalculatedPeriod(filters?.period)
    if (!calculated) return null
    const [from, to] = calculated
    return {
      preset: filters?.period?.type === "custom" ? "custom" : "relative",
      range: [
        dayjs.isDayjs(from) ? from.format("YYYY-MM-DD") : from,
        dayjs.isDayjs(to) ? to.format("YYYY-MM-DD") : to
      ]
    }
  }, [filters?.period])

  return (
    <Flex gap={12} style={{ flexDirection: isMobile && "column", width: isMobile && "100%" }}>
      {!onlyPeriod && (
        <AlertsTypeToggle
          value={filters?.alertsType}
          onChange={handleChangeFilter}
          disabled={disabled}
        />
      )}

      <DropdownDateBlock
        id={"period"}
        customOverlayClassName={"dashboard-filters"}
        label={"Custom"}
        iconType={"calendar_days"}
        setDropdownFilters={handleChangeCustomDate}
        dropdownFilters={getCalculatedPeriod(filters?.period)}
        presetId={filters?.__preset_period}
        disabled={disabled}
        inactive={false}
        alwaysShowLabel={false}
        placement={placement}
      />

      {showCompare && (
        <CompareSelect
          currentValue={compareCurrentValue}
          value={filters?.compare}
          onChange={(val) => handleChangeFilter({ compare: val })}
          disabled={disabled || filters?.period === "all"}
          // On a phone CompareSelect keeps its own "bottom".
          placement={isMobile ? undefined : placement}
        />
      )}
    </Flex>
  )
}
